export interface ArticleBlockImage {
    src: string; alt: string; caption?: string; link?: string; displayWidth?: string; displayHeight?: string;
    roomImageId?: string | null; resourceId?: string | null;
}
interface BlockIdentity { id: string }
export type ArticleBlock = BlockIdentity & (
    { type: 'heading'; level: number; content: string } |
    { type: 'paragraph' | 'quote' | 'code' | 'table'; content: string } |
    { type: 'list'; style: 'ordered' | 'unordered'; items: string[] } |
    { type: 'image'; layout: 'grid' | 'masonry' | 'carousel'; images: ArticleBlockImage[] } |
    { type: 'link'; url: string; title?: string; content?: string } |
    { type: 'embed'; url: string } | { type: 'divider' } |
    { type: 'unsupported'; originalType: string; raw: unknown }
);
export interface ArticleDocument { schemaVersion: 2; blocks: ArticleBlock[] }

const canonical = (value: any): any => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object'
    ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;
function legacyId(block: any) {
    const { id, ...fields } = block;
    let hash = 2166136261;
    new TextEncoder().encode(JSON.stringify(canonical(fields))).forEach(byte => { hash = Math.imul(hash ^ byte, 16777619) >>> 0; });
    return `b-${hash.toString(16).padStart(8, '0')}`;
}
export function readArticleDocument(value: unknown): ArticleDocument {
    const document = typeof value === 'string' ? JSON.parse(value) : value;
    const input = document as any;
    if (!input || !Array.isArray(input.blocks) || ![undefined, 1, 2].includes(input.schemaVersion)) throw new Error('Unsupported article document');
    const seen = new Set<string>();
    return { schemaVersion: 2, blocks: input.blocks.map((raw: any): ArticleBlock => {
        if (!raw || typeof raw !== 'object') throw new Error('Invalid article block');
        const base = /^[a-zA-Z0-9_-]+$/.test(raw.id || '') ? raw.id : legacyId(raw);
        let id = base, suffix = 2;
        while (seen.has(id)) id = `${base}-${suffix++}`;
        seen.add(id);
        const block = { ...raw, id };
        if (input.schemaVersion !== 2) {
            if (['heading', 'paragraph', 'quote', 'code', 'table'].includes(block.type)) block.content ??= '';
            if (block.type === 'heading') block.level ??= 1;
            if (block.type === 'list') { block.items ??= []; block.style ??= 'unordered'; }
            if (block.type === 'image') { block.images = (block.images || []).map((image: any) => ({ ...image, alt: image.alt || '', src: image.src || '' })); block.layout ??= 'grid'; }
            if (['link', 'embed'].includes(block.type)) block.url ??= '';
        }
        let valid = false;
        switch (block.type) {
            case 'heading': valid = Number.isInteger(block.level) && block.level >= 1 && block.level <= 6 && typeof block.content === 'string'; break;
            case 'paragraph': case 'quote': case 'code': case 'table': valid = typeof block.content === 'string'; break;
            case 'list': valid = ['ordered', 'unordered'].includes(block.style) && Array.isArray(block.items) && block.items.every((item: unknown) => typeof item === 'string'); break;
            case 'image': valid = ['grid', 'masonry', 'carousel'].includes(block.layout) && Array.isArray(block.images) && block.images.every((image: any) => image && typeof image.alt === 'string' && (typeof image.src === 'string' || image.roomImageId)); break;
            case 'link': case 'embed': valid = typeof block.url === 'string'; break;
            case 'divider': valid = true; break;
        }
        return valid ? block : { id, type: 'unsupported', originalType: typeof raw.type === 'string' ? raw.type : 'invalid', raw };
    }) };
}

export function safeArticleUrl(value: string): string {
    if (!value || Array.from(value).some(character => character.charCodeAt(0) <= 32 || character === '\\')) return '';
    try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:', 'mailto:'].includes(url.protocol) && !url.username && !url.password ? value : '';
    } catch { return ''; }
}
export function articleOutline(value: string) {
    const document = new DOMParser().parseFromString(value, 'text/html');
    const used = new Set<string>();
    const headings = Array.from(document.querySelectorAll('h1,h2,h3,h4,h5,h6')).map(heading => {
        const title = heading.textContent?.trim() || '';
        const base = heading.id || `article-${legacyId({ type: 'heading', content: title })}`;
        let id = base, suffix = 2;
        while (used.has(id)) id = `${base}-${suffix++}`;
        used.add(id); heading.id = id;
        return { id, title, level: Number(heading.tagName.slice(1)) };
    }).filter(heading => heading.title);
    return { html: document.body.innerHTML, headings };
}
export function sanitizeArticleHtml(value: string): string {
    const document = new DOMParser().parseFromString(value, 'text/html');
    const allowed = new Set('p div span br hr strong b em i u s strike small h1 h2 h3 h4 h5 h6 ul ol li pre code blockquote a img figure figcaption table thead tbody tr th td iframe'.split(' '));
    for (const element of Array.from(document.body.querySelectorAll('*'))) {
        const tag = element.tagName.toLowerCase();
        if (['script', 'style', 'object', 'template', 'svg', 'math'].includes(tag)) { element.remove(); continue; }
        if (!allowed.has(tag)) { element.replaceWith(...Array.from(element.childNodes)); continue; }
        for (const attr of Array.from(element.attributes)) {
            if (['src', 'href'].includes(attr.name)) {
                const url = safeArticleUrl(attr.value);
                if (url) element.setAttribute(attr.name, url); else element.removeAttribute(attr.name);
            } else if (attr.name === 'id') {
                if (!/^[a-zA-Z0-9_-]+$/.test(attr.value)) element.removeAttribute(attr.name);
            } else if (attr.name === 'class') {
                element.className = attr.value.split(/\s+/).filter(name => name.startsWith('article-')).join(' ');
            } else if (['width', 'height', 'colspan', 'rowspan'].includes(attr.name)) {
                if (!/^\d{1,4}$/.test(attr.value)) element.removeAttribute(attr.name);
            } else if (!['alt', 'title', 'data-article-unsupported'].includes(attr.name)) element.removeAttribute(attr.name);
        }
        if (tag === 'a') element.setAttribute('rel', 'noopener noreferrer');
        if (tag === 'iframe') { element.setAttribute('sandbox', 'allow-scripts allow-presentation'); element.setAttribute('referrerpolicy', 'no-referrer'); }
    }
    return document.body.innerHTML;
}
